/**
 * projectUtils.js
 * 
 * Utilities for project creation and management.
 */

/**
 * Create a new project
 * @param {StorageManager} storage - Storage manager instance
 * @param {Object} projectData - Project data
 * @returns {Promise<string>} - New project ID
 */
export async function createNewProject(storage, projectData) {
  const { name, description, template } = projectData;
  
  // Create base project
  const projectId = await storage.createProject({
    name: name.trim(),
    description: description?.trim() || '',
    currentPhase: 'PLANNING',
    requirements: getTemplateRequirements(template),
    created: Date.now(),
    updated: Date.now()
  });
  
  // If using template, initialize with template components and files
  if (template && template !== 'blank') {
    await initializeTemplate(storage, projectId, template);
  }
  
  return projectId;
}

/**
 * Get template requirements based on template type
 * @param {string} template - Template type
 * @returns {string} - Template requirements
 */
function getTemplateRequirements(template) {
  // Templates with predefined requirements
  const templates = {
    blank: '',
    web: `Create a responsive web application with the following features:
- Modern, clean user interface using React
- Responsive design that works on mobile and desktop
- State management using React Context
- Form handling with validation
- Navigation with React Router
- Light and dark theme support
- Data fetching from an API
- Error handling and loading states`,

    api: `Create a RESTful API with the following features:
- Node.js with Express framework
- User authentication with JWT
- CRUD operations for resources
- Input validation
- Error handling middleware
- Database integration
- API documentation
- Rate limiting and security features`,

    mobile: `Create a mobile application with the following features:
- React Native UI components
- Navigation between screens
- State management
- Form handling with validation
- Data fetching from an API
- Offline support
- Authentication
- Responsive design for different device sizes`,

    desktop: `Create a desktop application with the following features:
- Electron framework for cross-platform support
- Modern UI with React
- File system operations
- Configuration management
- Menu and context menu support
- Window management
- Keyboard shortcuts
- Installation and auto-update functionality`,

    cli: `Create a command-line interface (CLI) tool with the following features:
- Node.js CLI framework
- Command parsing and validation
- Interactive prompts
- Progress indicators
- Colorful output
- Configuration management
- Error handling
- Documentation and help commands`
  };
  
  return templates[template] || templates.blank;
}

/**
 * Initialize a project with template components and files
 * @param {StorageManager} storage - Storage manager instance
 * @param {string} projectId - Project ID
 * @param {string} template - Template type
 * @returns {Promise<void>}
 */
async function initializeTemplate(storage, projectId, template) {
  // Template base components
  const baseComponents = [
    {
      id: "core",
      projectId,
      name: "Core",
      description: "Core application functionality",
      dependencies: []
    }
  ];
  
  // Get template-specific components
  const templateComponents = await getTemplateComponents(template, projectId);
  const components = [...baseComponents, ...templateComponents];
  
  // Save components
  for (const component of components) {
    await storage.saveComponent(component);
  }
  
  // Get template files
  const files = await getTemplateFiles(template, projectId, components);
  
  // Save files
  for (const file of files) {
    await storage.saveFile(file);
  }
}

/**
 * Get template-specific components
 * @param {string} template - Template type
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} - Template components
 */
async function getTemplateComponents(template, projectId) {
  // Define template-specific components
  const templates = {
    web: [
      {
        id: "ui",
        projectId,
        name: "UI Components",
        description: "User interface components",
        dependencies: ["core"]
      },
      {
        id: "state",
        projectId,
        name: "State Management",
        description: "Application state management",
        dependencies: ["core"]
      },
      {
        id: "router",
        projectId,
        name: "Routing",
        description: "Navigation and routing",
        dependencies: ["core", "ui"]
      },
      {
        id: "api",
        projectId,
        name: "API Integration",
        description: "Data fetching and API services",
        dependencies: ["core"]
      }
    ],
    api: [
      {
        id: "routes",
        projectId,
        name: "Routes",
        description: "API routes and controllers",
        dependencies: ["core"]
      },
      {
        id: "models",
        projectId,
        name: "Models",
        description: "Data models and schemas",
        dependencies: ["core"]
      },
      {
        id: "middleware",
        projectId,
        name: "Middleware",
        description: "Request processing middleware",
        dependencies: ["core"]
      },
      {
        id: "auth",
        projectId,
        name: "Authentication",
        description: "User authentication and authorization",
        dependencies: ["core", "models"]
      }
    ],
    mobile: [
      {
        id: "screens",
        projectId,
        name: "Screens",
        description: "Application screens",
        dependencies: ["core"]
      },
      {
        id: "navigation",
        projectId,
        name: "Navigation",
        description: "Screen navigation",
        dependencies: ["core", "screens"]
      },
      {
        id: "ui",
        projectId,
        name: "UI Components",
        description: "Reusable UI components",
        dependencies: ["core"]
      },
      {
        id: "services",
        projectId,
        name: "Services",
        description: "API and device services",
        dependencies: ["core"]
      }
    ],
    desktop: [
      {
        id: "main",
        projectId,
        name: "Main Process",
        description: "Electron main process",
        dependencies: ["core"]
      },
      {
        id: "renderer",
        projectId,
        name: "Renderer",
        description: "UI renderer process",
        dependencies: ["core"]
      },
      {
        id: "ipc",
        projectId,
        name: "IPC",
        description: "Inter-process communication",
        dependencies: ["core", "main", "renderer"]
      },
      {
        id: "fs",
        projectId,
        name: "File System",
        description: "File system operations",
        dependencies: ["core", "main"]
      }
    ],
    cli: [
      {
        id: "commands",
        projectId,
        name: "Commands",
        description: "CLI commands",
        dependencies: ["core"]
      },
      {
        id: "ui",
        projectId,
        name: "UI",
        description: "Terminal UI components",
        dependencies: ["core"]
      },
      {
        id: "utils",
        projectId,
        name: "Utilities",
        description: "Helper utilities",
        dependencies: ["core"]
      }
    ]
  };
  
  return templates[template] || [];
}

/**
 * Get template-specific files
 * @param {string} template - Template type
 * @param {string} projectId - Project ID
 * @param {Array} components - Project components
 * @returns {Promise<Array>} - Template files
 */
async function getTemplateFiles(template, projectId, components) {
  // Define basic starter files for each template
  const coreFiles = [
    {
      projectId,
      path: "README.md",
      language: "markdown",
      estimatedLoc: 20,
      description: "Project documentation",
      componentId: "core"
    },
    {
      projectId,
      path: "package.json",
      language: "json",
      estimatedLoc: 30,
      description: "Project dependencies and scripts",
      componentId: "core"
    }
  ];
  
  // Get component ID map for easy lookup
  const componentMap = {};
  components.forEach(component => {
    componentMap[component.id] = component;
  });
  
  // Get template-specific files
  let templateFiles = [];
  
  switch (template) {
    case 'web':
      templateFiles = [
        {
          projectId,
          path: "src/index.js",
          language: "javascript",
          estimatedLoc: 15,
          description: "Entry point",
          componentId: "core"
        },
        {
          projectId,
          path: "src/App.js",
          language: "javascript",
          estimatedLoc: 25,
          description: "Main application component",
          componentId: "core"
        },
        {
          projectId,
          path: "src/components/Header.js",
          language: "javascript",
          estimatedLoc: 30,
          description: "Header component",
          componentId: componentMap.ui ? "ui" : "core"
        }
      ];
      break;
      
    case 'api':
      templateFiles = [
        {
          projectId,
          path: "src/index.js",
          language: "javascript",
          estimatedLoc: 20,
          description: "Entry point",
          componentId: "core"
        },
        {
          projectId,
          path: "src/config.js",
          language: "javascript",
          estimatedLoc: 15,
          description: "Configuration",
          componentId: "core"
        },
        {
          projectId,
          path: "src/routes/index.js",
          language: "javascript",
          estimatedLoc: 10,
          description: "Route definitions",
          componentId: componentMap.routes ? "routes" : "core"
        }
      ];
      break;
      
    case 'mobile':
      templateFiles = [
        {
          projectId,
          path: "App.js",
          language: "javascript",
          estimatedLoc: 25,
          description: "Entry point",
          componentId: "core"
        },
        {
          projectId,
          path: "src/screens/HomeScreen.js",
          language: "javascript",
          estimatedLoc: 30,
          description: "Home screen",
          componentId: componentMap.screens ? "screens" : "core"
        }
      ];
      break;
      
    case 'desktop':
      templateFiles = [
        {
          projectId,
          path: "main.js",
          language: "javascript",
          estimatedLoc: 35,
          description: "Main process entry point",
          componentId: componentMap.main ? "main" : "core"
        },
        {
          projectId,
          path: "renderer.js",
          language: "javascript",
          estimatedLoc: 20,
          description: "Renderer process entry point",
          componentId: componentMap.renderer ? "renderer" : "core"
        }
      ];
      break;
      
    case 'cli':
      templateFiles = [
        {
          projectId,
          path: "bin/index.js",
          language: "javascript",
          estimatedLoc: 15,
          description: "CLI entry point",
          componentId: "core"
        },
        {
          projectId,
          path: "src/commands/help.js",
          language: "javascript",
          estimatedLoc: 25,
          description: "Help command",
          componentId: componentMap.commands ? "commands" : "core"
        }
      ];
      break;
      
    default:
      // No additional files for blank template
      break;
  }
  
  return [...coreFiles, ...templateFiles];
}

/**
 * Export project to a downloadable file
 * @param {StorageManager} storage - Storage manager instance
 * @param {string} projectId - Project ID
 * @returns {Promise<Blob>} - Project export blob
 */
export async function exportProject(storage, projectId) {
  const blob = await storage.exportProject(projectId);
  return blob;
}

/**
 * Import project from a file
 * @param {StorageManager} storage - Storage manager instance
 * @param {File} file - Project file
 * @returns {Promise<string>} - Imported project ID
 */
export async function importProject(storage, file) {
  const projectId = await storage.importProject(file);
  return projectId;
}
